import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Gift, ArrowRight, Check, Heart, Envelope,
  User, CreditCard, Lock, Sparkle
} from '@phosphor-icons/react';
import GlobalNav from '../../components/GlobalNav';
import { GiftCard } from '../../components/PremiumCard';
import { useLanguage } from '../../contexts/LanguageContext';

const GiftOffer = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [selectedAmount, setSelectedAmount] = useState(50);
  const [step, setStep] = useState(1);

  const amounts = [10, 25, 50, 75, 100, 150, 200];

  const features = [
    t('gift_feature_1'),
    t('gift_feature_2'),
    t('gift_feature_3'),
    t('gift_feature_4')
  ];

  return (
    <div className="min-h-screen bg-[#0F1A14] text-white">
      <GlobalNav />

      {/* Hero */}
      <section className="pt-32 pb-12 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center gap-2 bg-[#C3A365]/10 border border-[#C3A365]/30 px-4 py-2 rounded-full mb-8"
          >
            <Gift size={16} className="text-[#C3A365]" weight="fill" />
            <span className="text-sm text-[#C3A365] font-medium">{t('gift_badge')}</span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-4xl sm:text-5xl font-light tracking-tight mb-6"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
          >
            {t('gift_title_1')}<br />
            <span className="text-[#C3A365]">{t('gift_title_2')}</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-lg text-[#8BA89A] max-w-xl mx-auto"
          >
            {t('gift_subtitle')}
          </motion.p>
        </div>
      </section>

      {/* Gift Card Builder */}
      <section className="px-6 pb-24">
        <div className="max-w-4xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-8">
            {/* Card Preview */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="lg:sticky lg:top-32"
            >
              <GiftCard value={selectedAmount} size="md" />

              {/* Features */}
              <div className="mt-8 space-y-3">
                {features.map((feature, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <Check size={16} className="text-[#C3A365]" weight="bold" />
                    <span className="text-[#8BA89A] text-sm">{feature}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Form */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="bg-[#18261E] border border-[#283D32] p-6 sm:p-8"
            >
              {/* Progress */}
              <div className="flex items-center gap-2 mb-8">
                {[1, 2, 3].map((s) => (
                  <div
                    key={s}
                    className={`flex-1 h-1 rounded-full transition-colors ${
                      s <= step ? 'bg-[#C3A365]' : 'bg-[#283D32]'
                    }`}
                  />
                ))}
              </div>

              {step === 1 && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                >
                  <h3 className="text-xl font-medium mb-6">{t('gift_step_1_title')}</h3>
                  
                  <div className="grid grid-cols-4 gap-3 mb-6">
                    {amounts.map((amount) => (
                      <button
                        key={amount}
                        onClick={() => setSelectedAmount(amount)}
                        className={`aspect-square flex items-center justify-center border transition-all ${
                          selectedAmount === amount
                            ? 'bg-[#C3A365] border-[#C3A365] text-[#0F1A14]'
                            : 'bg-[#1E2F26] border-[#283D32] text-white hover:border-[#C3A365]/50'
                        }`}
                      >
                        <span className="font-medium">£{amount}</span>
                      </button>
                    ))}
                    <button className="aspect-square flex items-center justify-center border border-[#283D32] bg-[#1E2F26] text-[#8BA89A] text-xs hover:border-[#C3A365]/50 transition-colors">
                      {t('gift_custom')}
                    </button>
                  </div>

                  <button
                    onClick={() => setStep(2)}
                    className="w-full py-4 bg-[#C3A365] text-[#0F1A14] font-semibold flex items-center justify-center gap-2"
                  >
                    {t('gift_continue')}
                    <ArrowRight size={18} />
                  </button>
                </motion.div>
              )}

              {step === 2 && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                >
                  <h3 className="text-xl font-medium mb-6">{t('gift_step_2_title')}</h3>
                  
                  <div className="space-y-4 mb-6">
                    <div>
                      <label className="text-sm text-[#8BA89A] block mb-2">{t('gift_recipient_name')}</label>
                      <div className="relative">
                        <User size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#8BA89A]" />
                        <input
                          type="text"
                          placeholder="Marie Dupont"
                          className="w-full pl-12 pr-4 py-3 bg-[#1E2F26] border border-[#283D32] text-white placeholder:text-[#8BA89A]/50 focus:border-[#C3A365]/50 focus:outline-none"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label className="text-sm text-[#8BA89A] block mb-2">{t('gift_recipient_email')}</label>
                      <div className="relative">
                        <Envelope size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#8BA89A]" />
                        <input
                          type="email"
                          placeholder="marie@email.com"
                          className="w-full pl-12 pr-4 py-3 bg-[#1E2F26] border border-[#283D32] text-white placeholder:text-[#8BA89A]/50 focus:border-[#C3A365]/50 focus:outline-none"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="text-sm text-[#8BA89A] block mb-2">{t('gift_message')}</label>
                      <textarea
                        rows={3}
                        placeholder={t('gift_message_placeholder')}
                        className="w-full px-4 py-3 bg-[#1E2F26] border border-[#283D32] text-white placeholder:text-[#8BA89A]/50 focus:border-[#C3A365]/50 focus:outline-none resize-none"
                      />
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <button
                      onClick={() => setStep(1)}
                      className="flex-1 py-4 border border-[#283D32] text-white"
                    >
                      {t('gift_back')}
                    </button>
                    <button
                      onClick={() => setStep(3)}
                      className="flex-1 py-4 bg-[#C3A365] text-[#0F1A14] font-semibold flex items-center justify-center gap-2"
                    >
                      {t('gift_continue')}
                      <ArrowRight size={18} />
                    </button>
                  </div>
                </motion.div>
              )}

              {step === 3 && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                >
                  <h3 className="text-xl font-medium mb-6">{t('gift_step_3_title')}</h3>
                  
                  {/* Summary */}
                  <div className="bg-[#1E2F26] p-4 mb-6">
                    <div className="flex justify-between mb-2">
                      <span className="text-[#8BA89A]">{t('gift_card_value')}</span>
                      <span className="text-white">£{selectedAmount}</span>
                    </div>
                    <div className="flex justify-between mb-2">
                      <span className="text-[#8BA89A]">{t('gift_fees')}</span>
                      <span className="text-[#C3A365]">{t('gift_free')}</span>
                    </div>
                    <div className="border-t border-[#283D32] pt-2 mt-2 flex justify-between">
                      <span className="text-white font-medium">{t('gift_total')}</span>
                      <span className="text-white font-medium">£{selectedAmount}</span>
                    </div>
                  </div>

                  <div className="space-y-4 mb-6">
                    <div>
                      <label className="text-sm text-[#8BA89A] block mb-2">{t('gift_card_number')}</label>
                      <div className="relative">
                        <CreditCard size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#8BA89A]" />
                        <input
                          type="text"
                          placeholder="4242 4242 4242 4242"
                          className="w-full pl-12 pr-4 py-3 bg-[#1E2F26] border border-[#283D32] text-white placeholder:text-[#8BA89A]/50 focus:border-[#C3A365]/50 focus:outline-none"
                        />
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm text-[#8BA89A] block mb-2">{t('gift_expiry')}</label>
                        <input
                          type="text"
                          placeholder="MM/YY"
                          className="w-full px-4 py-3 bg-[#1E2F26] border border-[#283D32] text-white placeholder:text-[#8BA89A]/50 focus:border-[#C3A365]/50 focus:outline-none"
                        />
                      </div>
                      <div>
                        <label className="text-sm text-[#8BA89A] block mb-2">CVC</label>
                        <input
                          type="text"
                          placeholder="123"
                          className="w-full px-4 py-3 bg-[#1E2F26] border border-[#283D32] text-white placeholder:text-[#8BA89A]/50 focus:border-[#C3A365]/50 focus:outline-none"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 mb-6 text-[#8BA89A] text-xs">
                    <Lock size={14} />
                    <span>{t('gift_secure_payment')}</span>
                  </div>

                  <div className="flex gap-3">
                    <button
                      onClick={() => setStep(2)}
                      className="flex-1 py-4 border border-[#283D32] text-white"
                    >
                      {t('gift_back')}
                    </button>
                    <button
                      onClick={() => navigate('/login')}
                      className="flex-1 py-4 bg-[#C3A365] text-[#0F1A14] font-semibold flex items-center justify-center gap-2"
                    >
                      <Heart size={18} weight="fill" />
                      {t('gift_send')}
                    </button>
                  </div>
                </motion.div>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Note */}
      <section className="px-6 pb-24">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 p-4 bg-[#18261E] border border-[#283D32] rounded-lg">
            <Sparkle size={20} className="text-[#C3A365]" />
            <p className="text-[#8BA89A] text-sm">
              {t('gift_note')}
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-[#1E2F26]">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row justify-between items-center gap-4">
          <span className="brand-logo text-xl text-[#8BA89A]">habitué</span>
          <p className="text-xs text-[#8BA89A]">© 2025 habitué. {t('footer_rights')}</p>
        </div>
      </footer>
    </div>
  );
};

export default GiftOffer;
